import IconLink from '@apollo/icons/default/IconLink.svg';
import {
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import { Switch } from '@apollo/space-kit/Switch';
import classnames from 'classnames';
import { useFormik } from 'formik';
import React, { useCallback } from 'react';
import * as Yup from 'yup';

import { isEmbeddableSandboxRoute } from 'src/app/embeddableSandbox/isEmbeddableSandboxRoute';
import { useEndpointReachability } from 'src/app/graph/hooks/useEndpointReachability';
import { GraphBehavioralFlags } from 'src/app/graph/variantLayout/graphBehavioralFlags/graphBehavioralFlags';
import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { Form } from 'src/components/common/form/Form';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { HeadersEditor } from 'src/components/headersEditor/HeadersEditor';
import { NewHeaderButton } from 'src/components/headersEditor/NewHeaderButton';
import { SelectWithLabel } from 'src/components/support-request/SupportRequest';
import { URLField } from 'src/components/urlField/URLField';
import { useAriaDescribedBySetting } from 'src/hooks/useAriaDescribedBySetting';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import Config from 'src/lib/config';
import { isLocalUrl } from 'src/lib/isLocalUrl';
import { isValidUrl } from 'src/lib/isValidUrl';

import { usePerKeyLocalStorage } from '../../../../../hooks/usePerKeyLocalStorage';
import { ExplorerBehavioralFlags } from '../../explorerBehavioralFlags';
import { useHeadersManagerContext } from '../../hooks/useExplorerState/useHeadersManagerContext/useHeadersManagerContext';
import {
  useLocalStorageGraphIdentifier,
  usePerGraphIdentifierLocalStorage,
} from '../../hooks/usePerGraphIdentifierLocalStorage';

/**
 * Schema for the form
 */
const validationSchema = Yup.object().shape({
  url: Yup.string().test(
    'valid-http-url',
    'Please enter a valid URL',
    (value: string) => {
      return !value || isValidUrl(value);
    },
  ),
  subscriptionUrl: Yup.string().test(
    'valid-http-or-ws-subscription-url',
    'Please enter a valid URL',
    (value: string) => {
      return (
        !value ||
        isValidUrl(value, { protocols: ['ws', 'wss', 'http', 'https'] })
      );
    },
  ),
  protocol: Yup.string()
    .oneOf([
      'auto-detect',
      'http-multipart',
      'graphql-ws',
      'subscriptions-transport-ws',
    ])
    .required(),
  sendCookies: Yup.boolean().required(),
  shouldAutoUpdateSchema: Yup.boolean().required(),
});

/**
 * Types inferred from `validationSchema`
 */
type FormValues = Yup.InferType<typeof validationSchema>;

export type SandboxConnectionSettings = {
  subscriptionUrl: string;
  sendCookies: boolean;
  shouldAutoUpdateSchema: boolean;
};

export const DEFAULT_LOCAL_WS_URL = 'ws://localhost:4000'; // nosemgrep
export const DEFAULT_LOCAL_HTTP_URL = 'http://localhost:4000';

/**
 * Modal to add, remove, or edit the URL of a schema variant
 */
export const SandboxConnectionSettingsModal = ({
  isOpen,
  onClose,
  explorerBehavioralFlags,
  graphBehavioralFlags,
}: {
  isOpen: boolean;
  onClose: () => void;
  explorerBehavioralFlags?: ExplorerBehavioralFlags;
  graphBehavioralFlags: GraphBehavioralFlags;
}) => {
  const [sandboxUrl, setSandboxUrl] = useLocalStorage('sandboxUrl');
  const [sandboxConnectionSettings, _, setSandboxConnectionSettingsForKey] =
    usePerKeyLocalStorage({
      key: sandboxUrl,
      localStorageKey: 'sandboxConnectionSettings',
      initialValue:
        graphBehavioralFlags.sandboxConnectionSettingsInitialValue(sandboxUrl),
    });
  const localStorageGraphIdentifier = useLocalStorageGraphIdentifier({
    shouldNamespaceSandboxByEndpoint: true,
    shouldOnlyStoreForRegisteredGraphs: false,
  });
  const [initialProtocol, setProtocol] = usePerGraphIdentifierLocalStorage({
    key: 'graphqlSubscriptionProtocol',
    graphIdentifier: localStorageGraphIdentifier,
    stableInitialValue: 'auto-detect',
  });

  const { sharedHeaderDefinitions, setSharedHeaderDefinitions } =
    useHeadersManagerContext();

  const [sharedHeaders, setSharedHeaders] = React.useState(
    sharedHeaderDefinitions,
  );
  React.useEffect(() => {
    if (sharedHeaderDefinitions) {
      setSharedHeaders(sharedHeaderDefinitions);
    }
  }, [sharedHeaderDefinitions]);

  const onSubmit = useCallback(
    (newConnectionSettings: FormValues) => {
      setSandboxConnectionSettingsForKey(
        {
          subscriptionUrl: newConnectionSettings.subscriptionUrl,
          sendCookies: newConnectionSettings.sendCookies,
          shouldAutoUpdateSchema: newConnectionSettings.shouldAutoUpdateSchema,
        },
        newConnectionSettings.url,
      );
      if (
        newConnectionSettings.protocol === 'auto-detect' ||
        newConnectionSettings.protocol === 'graphql-ws' ||
        newConnectionSettings.protocol === 'subscriptions-transport-ws' ||
        newConnectionSettings.protocol === 'http-multipart'
      ) {
        setProtocol(newConnectionSettings.protocol);
      }
      setSandboxUrl(newConnectionSettings.url);
      setSharedHeaderDefinitions(sharedHeaders);
      onClose();
    },
    [
      setSandboxConnectionSettingsForKey,
      setProtocol,
      setSandboxUrl,
      setSharedHeaderDefinitions,
      sharedHeaders,
      onClose,
    ],
  );

  const {
    errors,
    values,
    handleBlur,
    handleSubmit,
    isSubmitting,
    setFieldValue,
  } = useFormik<FormValues>({
    validateOnBlur: true,
    validateOnChange: false,
    initialValues: {
      url: sandboxUrl ?? '',
      subscriptionUrl: sandboxConnectionSettings.subscriptionUrl ?? '',
      sendCookies: sandboxConnectionSettings.sendCookies,
      shouldAutoUpdateSchema: sandboxConnectionSettings.shouldAutoUpdateSchema,
      protocol: initialProtocol,
    },
    onSubmit,
    validationSchema,
  });

  const describedBySetting = useAriaDescribedBySetting(
    'connection-setting-description-',
  );

  const reachability = useEndpointReachability();

  const handleOnChangeEndpointUrl = useCallback(
    (href: string) => setFieldValue('url', href),
    [setFieldValue],
  );

  const handleOnChangeSubscriptionUrl = useCallback(
    (href: string) => setFieldValue('subscriptionUrl', href),
    [setFieldValue],
  );

  const handleOnChangeGraphProtocol = useCallback(
    (newProtocol: string) => setFieldValue('protocol', newProtocol),
    [setFieldValue],
  );

  const subscriptionsSupported =
    !isEmbeddableSandboxRoute() ||
    explorerBehavioralFlags?.subscriptionsSupported;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <Form onSubmit={handleSubmit} noValidate className="w-auto max-w-xl">
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Connection settings</ModalTitle>
            <ModalDescription>
              Update the connection settings for your Sandbox
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            <div>
              <Switch
                isSelected={values.shouldAutoUpdateSchema}
                onChange={(isOn) => {
                  setFieldValue('shouldAutoUpdateSchema', isOn);
                }}
                className="w-full"
                data-analytics-label={`dev-graph-auto-update-switch ${
                  sandboxConnectionSettings.shouldAutoUpdateSchema
                    ? 'on'
                    : 'off'
                }`}
                data-analytics-category="Explorer"
              >
                Auto Update
              </Switch>
              <div
                className={classnames(
                  'pt-2 text-sm',
                  reachability === 'reachable'
                    ? 'text-secondary'
                    : 'text-placeholder',
                )}
              >
                {sandboxConnectionSettings.shouldAutoUpdateSchema
                  ? `Sandbox is polling your endpoint for schema changes every ${
                      isLocalUrl(values.url ?? '') ? 'second.' : '5 seconds.'
                    }`
                  : 'Sandbox is not polling for schema changes.'}
              </div>
            </div>
            {graphBehavioralFlags.sandboxEndpointIsEditable ? (
              <FormControl isInvalid={!!errors.url}>
                <FormLabel>Endpoint</FormLabel>
                <URLField
                  autoFocus
                  isDisabled={isSubmitting}
                  name="url"
                  onBlur={handleBlur}
                  onChange={handleOnChangeEndpointUrl}
                  placeholder={DEFAULT_LOCAL_HTTP_URL}
                  value={values.url}
                  isSocket={false}
                />
                <FormErrorMessage>{errors.url}</FormErrorMessage>
              </FormControl>
            ) : (
              <div>
                <label className="mb-2 text-base font-semibold">Endpoint</label>
                <div className="flex items-center p-2.5 text-base">
                  <IconLink className="mr-2" />
                  <ClickableText
                    as="a"
                    className="overflow-auto text-ellipsis whitespace-nowrap text-link"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={values.url ?? DEFAULT_LOCAL_HTTP_URL}
                  >
                    {values.url ?? DEFAULT_LOCAL_HTTP_URL}
                  </ClickableText>
                </div>
              </div>
            )}
            <Tooltip
              disabled={subscriptionsSupported}
              label="Subscriptions in the embedded Sandbox are not supported in this version of the embed. Try updating to the latest version."
              placement="top"
            >
              {graphBehavioralFlags.sandboxEndpointIsEditable ? (
                <div className="flex justify-between gap-3">
                  <FormControl className="w-80">
                    <FormLabel>Subscriptions</FormLabel>
                    <Input
                      name="subscriptionUrl"
                      onBlur={handleBlur}
                      onChange={(event) =>
                        handleOnChangeSubscriptionUrl(event.target.value)
                      }
                      placeholder={DEFAULT_LOCAL_WS_URL}
                      value={values.subscriptionUrl}
                      leftElement={<IconLink />}
                    />

                    {/* nosemgrep */}
                    {values.subscriptionUrl.startsWith('ws://') &&
                    !(
                      'ws://localhost'.startsWith(values.subscriptionUrl) ||
                      'ws://127.0.0.1'.startsWith(values.subscriptionUrl)
                    ) ? (
                      <FormHelperText iconType="warning">
                        Most browsers disallow secure websites making requests
                        {/* nosemgrep */}
                        over insecure protocols such as ws:// - consider using
                        the encrypted wss:// instead
                      </FormHelperText>
                    ) : undefined}
                    <FormErrorMessage>
                      {errors.subscriptionUrl}
                    </FormErrorMessage>
                  </FormControl>

                  <SelectWithLabel
                    onBlur={handleBlur}
                    matchTriggerWidth={false}
                    placement="bottom-end"
                    className="w-44"
                    truncate={true}
                    disabled={isSubmitting || !subscriptionsSupported}
                    renderTriggerNode={(selectedItem) =>
                      selectedItem?.value || values.protocol
                    }
                    onChange={(event) => {
                      handleOnChangeGraphProtocol(event.target.value);
                    }}
                    value={values.protocol}
                    label={<FormLabel>Implementation</FormLabel>}
                  >
                    <option className="h-auto" value="auto-detect">
                      <div>Auto-detect</div>
                      <div className="flex flex-col opacity-50">
                        Automatically determines which protocol to use
                      </div>
                    </option>
                    <option className="h-auto" value="graphql-ws">
                      <div>graphql-ws</div>
                      <div className="flex flex-col opacity-50">
                        Implements the "graphql-transport-ws" protocol
                      </div>
                    </option>
                    <option
                      className="h-auto"
                      value="subscriptions-transport-ws"
                    >
                      <div>subscriptions-transport-ws</div>
                      <div className="flex flex-col opacity-50">
                        Implements the "graphql-ws" protocol
                      </div>
                    </option>
                    <option className="h-auto" value="http-multipart">
                      <div>Multipart subscriptions</div>
                      <div className="flex flex-col opacity-50">
                        {
                          // TODO add a link to docs when ready
                        }
                        Implements Apollo's multipart subscription protocol
                      </div>
                    </option>
                  </SelectWithLabel>
                </div>
              ) : (
                <div className="mb-2">
                  <label className="mb-2 text-base font-semibold">
                    Subscription Endpoint
                  </label>
                  <div className="flex items-center p-2.5 text-base">
                    <IconLink className="mr-2 size-4 text-placeholder" />
                    <ClickableText
                      as="a"
                      className="overflow-auto text-ellipsis whitespace-nowrap text-link"
                      target="_blank"
                      rel="noopener noreferrer"
                      href={values.subscriptionUrl ?? DEFAULT_LOCAL_WS_URL}
                    >
                      {values.subscriptionUrl ?? DEFAULT_LOCAL_WS_URL}
                    </ClickableText>
                  </div>
                </div>
              )}
            </Tooltip>
            <div>
              {graphBehavioralFlags.showSandboxConnectionSettingsCookieToggle && (
                <>
                  <Switch
                    isSelected={values.sendCookies}
                    onChange={(value) => setFieldValue('sendCookies', value)}
                    aria-describedby={describedBySetting}
                  >
                    Include cookies
                  </Switch>
                  {values.sendCookies && (
                    <div
                      className="mt-2 text-sm text-secondary"
                      aria-hidden
                      id={describedBySetting}
                    >
                      To use cookies in requests
                      <ol>
                        <li className="mt-2">
                          The cookie's value must contain{' '}
                          <ClickableText
                            className="font-semibold underline"
                            href="https://www.chromium.org/updates/same-site"
                            target="_blank"
                            rel="noopener noreferrer"
                            as="a"
                          >
                            SameSite=None; Secure
                          </ClickableText>
                        </li>
                        <li className="mt-2">
                          These CORS headers need to be present in your server's
                          response to Studio:
                          <div className="mt-2 break-words rounded bg-secondary p-2 font-mono">
                            Access-Control-Allow-Origin: https://
                            {Config.appHost}
                            <br />
                            Access-Control-Allow-Credentials: true
                          </div>
                        </li>
                      </ol>
                    </div>
                  )}
                </>
              )}
            </div>

            <div>
              <div className="font-semibold">Shared headers</div>
              <HeadersEditor
                className="mt-2"
                headerDefinitions={sharedHeaders}
                setHeaderDefinitions={setSharedHeaders}
                withCheckbox={false}
                // TODO - the HeadersEditor component is confusingly used in Studio.
                // It was made for the Explorer which shows both shared & tab headers.
                // It is abused here to show just shared headers. Passing shared headers
                // in headerDefinitions & nothing to `sharedHeaders`. This component
                // should be split up so we can pass props that make sense for just
                // shared headers
                headersDisabled={!graphBehavioralFlags.canEditSharedHeaders}
                headersDisabledTooltipContent={
                  !graphBehavioralFlags ||
                  graphBehavioralFlags.canEditSharedHeaders
                    ? 'Click to edit shared headers in connection settings'
                    : "You don't have permission to edit the shared headers."
                }
                sharedHeadersDisabled={false}
              />
              {!sharedHeaders.length && (
                <div className="text-sm text-secondary">
                  No shared headers set
                </div>
              )}
              {graphBehavioralFlags.canEditSharedHeaders && (
                <NewHeaderButton
                  className="mt-2"
                  isSharedHeader={true}
                  onClick={() =>
                    setSharedHeaders(
                      sharedHeaders.concat({
                        headerName: '',
                        value: '',
                        checked: true,
                        key: Math.random(),
                      }),
                    )
                  }
                />
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button
                variant="secondary"
                isDisabled={isSubmitting}
                onClick={onClose}
                type="button"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                isLoading={isSubmitting}
                loadingText="Save"
                type="submit"
                data-testid="sandbox-connection-settings-modal-save-button"
              >
                Save
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Form>
    </Modal>
  );
};
